import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'

// IMPORTANT: Do not import anything from '@/lib/apiClient' inside '@/store/index.ts'
// or any Redux slice. A circular import will cause `store` to resolve as `undefined`
// in the interceptor. For API calls in thunks, pass the client as a thunk argument.
import { store } from '@/store'
import { logout } from '@/store/authSlice'
import { normalizeError } from './apiErrors'

// API clients do not set a baseURL. All URLs must be absolute, built by urlBuilder.
export function createApiClient() {
	const instance: AxiosInstance = axios.create({
		timeout: 15_000,
		headers: {
			'Content-Type': 'application/json',
		},
	})

	// Request interceptor — reject relative URLs; attach JWT when available.
	instance.interceptors.request.use(
		(config: InternalAxiosRequestConfig) => {
			if (
				config.url &&
				!config.url.startsWith('http://') &&
				!config.url.startsWith('https://')
			) {
				return Promise.reject(
					new Error(
						`[apiClient] Relative URL detected: "${config.url}". All URLs must be absolute — use a urlBuilder function.`,
					),
				)
			}
			const token = store.getState().auth.token
			if (token) {
				config.headers.set('Authorization', `Bearer ${token}`)
			}
			return config
		},
		(error: unknown) => Promise.reject(normalizeError(error)),
	)

	// Response interceptor — normalize all non-2xx responses into ApiError.
	// On 401, dispatch logout to clear the Redux auth store.
	instance.interceptors.response.use(
		(response) => response,
		(error: unknown) => {
			const apiError = normalizeError(error)
			if (apiError.status === 401) {
				store.dispatch(logout())
			}
			return Promise.reject(apiError)
		},
	)

	return {
		/** Exposed for test mocking via axios-mock-adapter */
		instance,

		get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
			return instance.get<T>(url, config).then(({ data }) => data)
		},

		post<TBody, TResponse>(
			url: string,
			body?: TBody,
			config?: AxiosRequestConfig,
		): Promise<TResponse> {
			return instance.post<TResponse>(url, body, config).then(({ data }) => data)
		},

		put<TBody, TResponse>(
			url: string,
			body?: TBody,
			config?: AxiosRequestConfig,
		): Promise<TResponse> {
			return instance.put<TResponse>(url, body, config).then(({ data }) => data)
		},

		patch<TBody, TResponse>(
			url: string,
			body?: TBody,
			config?: AxiosRequestConfig,
		): Promise<TResponse> {
			return instance.patch<TResponse>(url, body, config).then(({ data }) => data)
		},

		// Named `del` because `delete` is a reserved keyword in JavaScript.
		// REST DELETE responses conventionally return 204 with no body; pass `T` explicitly
		// if the endpoint returns a response body.
		del<T = void>(url: string, config?: AxiosRequestConfig): Promise<T> {
			return instance.delete<T>(url, config).then(({ data }) => data)
		},
	}
}

export type ApiClient = ReturnType<typeof createApiClient>

export const oocApi = createApiClient()

export const oloApi = createApiClient()
